/**
 * Social media sharing utility for SAT score reports
 */

interface ShareData {
  totalScore: number
  calculationType: string
  mathScore?: number
  readingScore?: number
  testNumber?: string
  actScore?: number
  percentile?: number
}

export const socialShare = {
  /**
   * Share to Twitter/X
   */
  twitter: (data: ShareData) => {
    const text = generateShareText(data)
    const url = window.location.href
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    window.open(twitterUrl, '_blank', 'width=550,height=420')
  },

  /**
   * Share to Facebook
   */
  facebook: (data: ShareData) => {
    const url = window.location.href
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    window.open(facebookUrl, '_blank', 'width=550,height=420')
  },

  /**
   * Share to LinkedIn
   */
  linkedin: (data: ShareData) => {
    const text = generateShareText(data)
    const url = window.location.href
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`
    window.open(linkedinUrl, '_blank', 'width=550,height=420')
  },

  /**
   * Share to WhatsApp
   */
  whatsapp: (data: ShareData) => {
    const text = generateShareText(data)
    const url = window.location.href
    const fullText = `${text}\n\n${url}`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`
    window.open(whatsappUrl, '_blank')
  },

  /**
   * Copy link to clipboard
   */
  copyLink: async (data: ShareData) => {
    const text = generateShareText(data)
    const url = window.location.href
    const fullText = `${text}\n\n${url}`
    
    try {
      await navigator.clipboard.writeText(fullText)
      return true
    } catch (error) {
      console.error('Failed to copy:', error)
      return false
    }
  }
}

/**
 * Generate shareable text based on score data
 */
function generateShareText(data: ShareData): string {
  const { totalScore, calculationType, mathScore, readingScore, testNumber, actScore, percentile } = data

  if (calculationType === 'act_conversion') {
    return `🎯 I converted my ACT score of ${actScore} to SAT: ${totalScore}/1600 (${percentile}th percentile)! Check your score at SAT Score Calculator.`
  }

  if (calculationType === 'practice_test') {
    return `📚 ${testNumber} Result: ${totalScore}/1600 (Math: ${mathScore}, Reading: ${readingScore})! Calculate yours at SAT Score Calculator.`
  }

  if (calculationType === 'digital_sat' && data.hasOwnProperty('superscore')) {
    return `🎓 My SAT Superscore: ${totalScore}/1600! Improve your score with SAT Score Calculator.`
  }

  return `🎓 My SAT Score: ${totalScore}/1600 (Math: ${mathScore}, Reading: ${readingScore})! Calculate yours at SAT Score Calculator.`
}
